<script setup lang="ts">
import AvatarCouple from './AvatarCouple.vue';
import api from '~/api';
import type { FaceDetectData, Face } from '~/types';
import { useDisplay } from 'vuetify';
import TipsDialog from '@/components/TipsDialog.vue';
import appConfig from '~/app.config';
const snackbar = useSnackbarStore();
const comm = useCommStore();
const showFacePickerLoading = ref(false);
const display = useDisplay();
const isMobile = computed(() => display.xs.value);
const props = defineProps({
    faceDetectData: {
        type: Object as PropType<FaceDetectData>,
        required: true
    },
    creditNum: {
        type: Number,
        required: true
    },
    freeRemaining: {
        type: Number,
        default: 0
    },
    toolId: {
        type: String,
        required: true
    }
});

const emit = defineEmits(['startPolling']); // 定义自定义事件

const WIDTH = 256;

// 使用 useFaces 组合式函数
const { faces, isEmpty, loading, loadFaces, handleFileChange, moveToTop, removeFace, handleDragOver, handleDrop, handleDragLeave, isDragging, addFaceToList } = useFaces();
const globalLoading = useLoadingStore();

// 轮询第二阶段检测状态
const pollSecondDetection = async (materialId: string, taskId: string) => {
    let retryCount = 0;
    const maxRetries = 1000;
    const retryInterval = 3000; // 3秒轮询一次

    const getDetectionStatus = async () => {
        try {
            const response = await api.uploadDetectResult(materialId, taskId);

            switch (response.detect_status) {
                case 1: { // 检测完成
                    console.log('FaceFusion 人脸检测完成');

                    // 重新获取素材详情以获得最新的人脸信息
                    const material = await api.getMaterialDetail(materialId, props.toolId);

                    // 处理新的检测结果
                    let detectedFaces = [];
                    const detectInfo = material.detect_info || null;

                    if (detectInfo && Array.isArray(detectInfo) && detectInfo.length > 0) {
                        const firstItem = detectInfo[0];
                        // 新格式：包含 face 和 meta 字段
                        if ('face' in firstItem && 'meta' in firstItem) {
                            console.log('获取到新格式人脸数据，检测到', detectInfo.length, '个人脸');
                            detectedFaces = detectInfo.map((item: any) => ({
                                index: item.meta.pos,
                                imageUrl: item.face,
                                bounds: item.meta.bounds,
                                selectedFaceId: undefined
                            }));

                            // 更新 faceCouples 数据
                            faceCouples.value = detectedFaces.map((face: any) => ({
                                left: face.imageUrl,
                                coord: null,
                                right: '',
                                faceId: '',
                                index: face.index
                            }));
                        }
                    }

                    showFacePickerLoading.value = false;
                    isDetectionInProgress.value = false;
                    break;
                }
                case -1: // 检测失败
                    console.error('FaceFusion 人脸检测失败');
                    snackbar.showError('人脸检测失败，请重试');
                    showFacePickerLoading.value = false;
                    isDetectionInProgress.value = false;
                    break;
                case -2: // 违规内容
                    console.error('检测到违规内容');
                    snackbar.showError('检测到违规内容，请更换素材');
                    showFacePickerLoading.value = false;
                    isDetectionInProgress.value = false;
                    break;
                case 0: // 处理中
                    if (retryCount < maxRetries) {
                        retryCount++;
                        setTimeout(getDetectionStatus, retryInterval);
                    } else {
                        snackbar.showError('检测超时，请重试');
                        showFacePickerLoading.value = false;
                        isDetectionInProgress.value = false;
                    }
                    break;
                default:
                    snackbar.showError('未知状态码');
                    showFacePickerLoading.value = false;
                    isDetectionInProgress.value = false;
                    break;
            }
        } catch (error) {
            console.error('轮询检测状态失败:', error);
            if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(getDetectionStatus, retryInterval);
            } else {
                snackbar.showError('检测失败，请重试');
                showFacePickerLoading.value = false;
                isDetectionInProgress.value = false;
            }
        }
    };

    getDetectionStatus();
};

// 标记第二阶段检测是否已触发
const hasTriggeredSecondDetection = ref(false);
// 标记检测是否正在进行中
const isDetectionInProgress = ref(false);

// 触发第二阶段 FaceFusion 人脸检测
const triggerSecondStageDetection = async () => {
    // 避免重复触发
    if (hasTriggeredSecondDetection.value) {
        console.log('第二阶段检测已触发过，跳过');
        // 如果检测正在进行中，保持 loading 状态
        if (isDetectionInProgress.value) {
            showFacePickerLoading.value = true;
        } else {
            showFacePickerLoading.value = false;
        }
        return;
    }

    // 检查是否需要进行第二阶段人脸检测（FaceFusion）
    const needFaceFusionDetect = !props.faceDetectData.detectedFaces || props.faceDetectData.detectedFaces.length === 0;

    if (needFaceFusionDetect) {
        console.log('开始第二阶段 FaceFusion 人脸检测...');
        hasTriggeredSecondDetection.value = true;
        isDetectionInProgress.value = true;
        showFacePickerLoading.value = true;

        try {
            // 提交第二次检测任务（use_facefusion=true）
            const detectCreated = await api.uploadDetectCreate({
                materialId: props.faceDetectData.materialId,
                useFacefusion: true // 第二阶段检测，获取人脸信息
            });
            const { material_id, task_id } = detectCreated;

            // 开始轮询检测结果
            pollSecondDetection(material_id, task_id);
        } catch (error) {
            console.error('Failed to start FaceFusion detection:', error);
            snackbar.showError('Failed to start face detection, please try again');
            showFacePickerLoading.value = false;
            isDetectionInProgress.value = false;
            hasTriggeredSecondDetection.value = false; // 失败后允许重试
        }
    } else {
        console.log('已有人脸检测结果，无需重复检测');
        showFacePickerLoading.value = false;
    }
};

onMounted(async () => {
    try {
        await loadFaces();
        // 自动为第一个人脸选择第一个头像（如果有的话）
        if (faces.value.length > 0) {
            if (swapMode.value === 'single') {
                // 单人模式：自动选中第一个头像
                singleModeFace.value = faces.value[0];
            } else if (faceCouples.value.length > 0 && faceCouples.value[0]) {
                // 多人模式：为第一个检测到的人脸选择第一个头像
                faceCouples.value[0].right = faces.value[0].url;
                faceCouples.value[0].faceId = faces.value[0].face_id;
            }
        }
    } catch (error) {
        console.error('Failed to load faces:', error);
    }
});

onBeforeUnmount(() => {
    // 清理检测状态，防止内存泄漏
    showFacePickerLoading.value = false;
    hasTriggeredSecondDetection.value = false;
    isDetectionInProgress.value = false;
});

// 单人/多人换脸模式切换
const swapMode = ref<'single' | 'multiple'>('single');

// 初始化 faceCouples：显示素材中检测到的所有人脸
// 优先使用新格式 detectedFaces，否则使用旧格式 detectInfo
const initFaceCouples = () => {
    // 新格式：使用 FaceFusion 检测的人脸图片
    if (props.faceDetectData.detectedFaces && props.faceDetectData.detectedFaces.length > 0) {
        return props.faceDetectData.detectedFaces.map((face) => ({
            left: face.imageUrl,  // 直接使用检测到的人脸图片URL
            coord: null,  // 新格式不需要坐标
            right: '',
            faceId: '',
            index: face.index
        }));
    }
    // 旧格式：使用坐标裁剪
    else if (props.faceDetectData.detectInfo && props.faceDetectData.detectInfo.length > 0) {
        const firstItem = props.faceDetectData.detectInfo[0];
        // 检查是否为旧格式（包含 x, y 字段）
        if ('x' in firstItem || 'y' in firstItem) {
            return props.faceDetectData.detectInfo.map((info: any, index: number) => ({
                left: props.faceDetectData.detectUrl,
                coord: {
                    x: info.x,
                    y: info.y,
                    width: info.width,
                    height: info.height
                },
                right: '',
                faceId: '',
                index: index
            }));
        }
    }
    return [];
};

const faceCouples = ref(initFaceCouples());

// 当前选择的 faceCouple 的索引
const selectedCoupleIndex = ref(0);

// 单人换脸模式：当前选中的人脸
const singleModeFace = ref<Face | null>(null);

// 计算属性用于双向绑定
const selectedCouple = computed({
    get() {
        return faceCouples.value[selectedCoupleIndex.value];
    },
    set(value) {
        faceCouples.value[selectedCoupleIndex.value] = value;
    }
});

// 监听所有 faceCouples 的 right 属性的变化，当right清空时，同时需要清空 faceId
faceCouples.value.forEach((couple) => {
    watch(() => couple.right, (newValue) => {
        if (!newValue) {
            couple.faceId = '';
        }
    });
});

// 当前选中的人脸（根据模式不同，返回不同的值）
const currFace = computed(() => {
    if (swapMode.value === 'single') {
        // 单人模式：返回 singleModeFace
        return singleModeFace.value;
    } else {
        // 多人模式：返回当前 FaceCouple 选中的人脸
        if (!selectedCouple.value?.right) {
            return null;
        }
        return faces.value.find(item => item.url === selectedCouple.value.right);
    }
});

const handleFaceSelection = (face) => {
    if (swapMode.value === 'single') {
        // 单人模式：直接设置选中的人脸
        singleModeFace.value = face;
    } else {
        // 多人模式：为当前选中的 FaceCouple 设置人脸
        if (!selectedCouple.value) {
            return;
        }
        selectedCouple.value.right = face.url;
        bindFaceIdToFaceCouple(face.face_id, selectedCoupleIndex.value);
    }
};

// 处理 AvatarCouple 上传成功事件
const handleFaceUploaded = ({ url, faceId }) => {
    // 构造 Face 对象
    const newFace: Face = {
        uid: '', // API 返回可能不包含 uid，但前端展示暂时不需要
        face_id: faceId,
        url: url,
        created_at: new Date().toISOString(),
        toped_at: 0
    };

    // 立即添加到 faces 列表顶部
    addFaceToList(newFace);

    // 自动为当前选中的 FaceCouple 设置这个新上传的人脸
    if (swapMode.value === 'single') {
        singleModeFace.value = newFace;
    } else if (selectedCouple.value) {
        selectedCouple.value.right = url;
        bindFaceIdToFaceCouple(faceId, selectedCoupleIndex.value);
    }
};

// Models
const models = ref([
    {
        label: 'ShapeTransformer',
        value: 'ShapeTransformer'
    },
    {
        label: 'ShapeKeeper',
        value: 'ShapeKeeper'
    },
    {
        label: 'ShapeTransformer HD',
        value: 'ShapeTransformerHD'
    },
]);

// 存储当前选中的模型名称
const selectedModel = ref(models.value[0].value);
const selectModel = (modelValue: string) => {
    selectedModel.value = modelValue;
};

// 添加头像逻辑
const fileInput = ref<HTMLInputElement | null>(null);

const tipsDialogVisible = ref(false);
const dialogType = ref<'info' | 'upload' | 'v2beta'>('info');
const showTipsDialog = (type: 'info' | 'upload' | 'v2beta') => {
    if (type === 'upload' && !comm.appInfo.canShowTipsPopup) {
        triggerFileInput();
        return;
    }
    dialogType.value = type;
    tipsDialogVisible.value = true;
};

const handleTipsDialogConfirm = () => {
    if (dialogType.value === 'upload') {
        triggerFileInput();
    }
};

// 处理多人模式点击
const handleMultipleModeClick = async () => {
    // 检查视频时长限制：V2.0 仅支持 60 秒以内的视频
    const isVideo = appConfig.videoExt.some(v => v.ext === props.faceDetectData.format);
    const MAX_DURATION = 60; // 60 秒

    if (isVideo && props.faceDetectData.duration && props.faceDetectData.duration > MAX_DURATION) {
        // 视频超过 1 分钟，不允许使用 V2.0 多人换脸
        snackbar.showError(`V2.0 multi-face swap only supports videos under ${MAX_DURATION} seconds. Your video is ${Math.floor(props.faceDetectData.duration)} seconds.`);
        return;  // 阻止切换到 V2.0 模式
    }

    // 检查是否首次点击多人换脸
    if (!comm.appInfo.hasShownV2BetaTips) {
        // 首次点击，显示 V2.0 Beta 提示弹窗
        showTipsDialog('v2beta');
        // 标记已显示过
        comm.setAppInfo({ hasShownV2BetaTips: true });
    }

    // 切换到多人模式
    swapMode.value = 'multiple';

    // 检查是否已有人脸检测结果（优先检查素材的 detect_info）
    // 如果素材已经有 detect_info 数据，说明已经检测过了，不需要重复检测
    const hasDetectInfo = props.faceDetectData.detectInfo && Array.isArray(props.faceDetectData.detectInfo) && props.faceDetectData.detectInfo.length > 0;
    const hasDetectedFaces = props.faceDetectData.detectedFaces && props.faceDetectData.detectedFaces.length > 0;

    // 只有在既没有 detect_info 也没有 detectedFaces 的情况下才触发检测
    if (!hasDetectInfo && !hasDetectedFaces) {
        // 触发第二阶段人脸检测（仅在多人模式下需要）
        await triggerSecondStageDetection();
    } else if (hasDetectInfo && !hasDetectedFaces) {
        // 如果有 detect_info 但没有 detectedFaces，说明是从素材历史打开的
        // 直接使用 detect_info 初始化 faceCouples，无需重新检测
        console.log('使用素材中已有的 detect_info，无需重新检测');
    }
};

const triggerFileInput = () => {
    if (fileInput.value) {
        fileInput.value.click();
    }
};

// 绑定 faceId 到 faceCouple
const bindFaceIdToFaceCouple = (faceId: string, index: number) => {
    faceCouples.value[index].faceId = faceId;
};

// 人脸映射关系接口
interface FaceMapping {
    detected_face_url: string;  // 素材中检测到的人脸URL
    user_face_id: string;       // 用户选择的替换人脸ID
}

const generateBindFaceParam = (): FaceMapping[] => {
    const bindFace: FaceMapping[] = [];

    if (swapMode.value === 'single') {
        // 单人模式：只要用户选择了人脸就创建映射
        if (singleModeFace.value?.face_id) {
            // 优先使用新格式的 detectedFaces
            if (props.faceDetectData.detectedFaces && props.faceDetectData.detectedFaces.length > 0) {
                props.faceDetectData.detectedFaces.forEach(detectedFace => {
                    bindFace.push({
                        detected_face_url: detectedFace.imageUrl,
                        user_face_id: singleModeFace.value.face_id
                    });
                });
            }
            // V1.0单人模式：如果有 detectUrl 则使用
            else if (props.faceDetectData.detectUrl) {
                bindFace.push({
                    detected_face_url: props.faceDetectData.detectUrl,
                    user_face_id: singleModeFace.value.face_id
                });
            }
            // 如果 detectUrl 为空，但用户已选择头像，也允许提交
            // 使用空字符串作为占位符，后端会根据 materialId 处理
            else {
                bindFace.push({
                    detected_face_url: '',
                    user_face_id: singleModeFace.value.face_id
                });
            }
        }
    } else {
        // 多人模式：根据用户选择的配对关系生成映射
        faceCouples.value.forEach((couple) => {
            if (couple.faceId && couple.left) {
                bindFace.push({
                    detected_face_url: couple.left,  // 素材中检测到的人脸URL
                    user_face_id: couple.faceId      // 用户选择的人脸ID
                });
            }
        });
    }

    return bindFace;
};

// 检查是否至少选择了一个人脸
const hasSelectedFace = computed(() => {
    if (swapMode.value === 'single') {
        return singleModeFace.value !== null;
    } else {
        return faceCouples.value.some(couple => couple.faceId);
    }
});

const onFaceSwap = async () => {
    const bindFace = generateBindFaceParam();

    // 检查是否至少选择了一个人脸
    if (bindFace.length === 0) {
        snackbar.showError('Please select at least one face to swap');
        return;
    }

    try {
        globalLoading.startLoading('loading...');
        const resp = await api.createFaceSwap(props.faceDetectData.materialId, bindFace, props.toolId, swapMode.value);
        // 触发事件并传递数据给父组件，包含 swapMode 信息
        emit('startPolling', { orderId: resp.order_id, taskId: resp.task_id, pollInterval: resp.wait_interval, swapMode: swapMode.value });
    } catch (error) {
        console.error(error);
        snackbar.showError(error.response?.data?.message);
    } finally {
        globalLoading.stopLoading();
    }
};

watch(() => faces.value, (newFaces) => {
    // 如果当前没有选择头像，并且 faces 列表不为空，默认选中第一个头像
    if (!currFace.value && newFaces.length > 0) {
        if (swapMode.value === 'single') {
            // 单人模式：自动选中第一个头像
            singleModeFace.value = newFaces[0];
        } else if (faceCouples.value.length > 0) {
            // 多人模式：自动为第一个检测到的人脸选择第一个用户头像
            if (faceCouples.value[0] && !faceCouples.value[0].faceId) {
                faceCouples.value[0].right = newFaces[0].url;
                faceCouples.value[0].faceId = newFaces[0].face_id;
            }
        }
    }
}, { immediate: true });

// 监听模式切换，进行相应的初始化
watch(() => swapMode.value, (newMode, oldMode) => {
    if (newMode === 'single' && faces.value.length > 0 && !singleModeFace.value) {
        // 切换到单人模式时，如果还没有选择人脸，自动选中第一个
        singleModeFace.value = faces.value[0];
    } else if (newMode === 'multiple' && faceCouples.value.length > 0 && !faceCouples.value[0]?.faceId && faces.value.length > 0) {
        // 切换到多人模式时，如果第一个位置还没有选择人脸，自动选中第一个
        faceCouples.value[0].right = faces.value[0].url;
        faceCouples.value[0].faceId = faces.value[0].face_id;
    }

    // 从 v2.0 切回 v1.0 时，始终清除加载状态（v1.0 不需要 loading）
    if (oldMode === 'multiple' && newMode === 'single') {
        showFacePickerLoading.value = false;
    }

    // 从 v1.0 切换到 v2.0 时，如果检测正在进行中，恢复 loading 状态
    if (oldMode === 'single' && newMode === 'multiple') {
        if (isDetectionInProgress.value) {
            showFacePickerLoading.value = true;
        }
    }
});
</script>


<template>
    <v-container class="p-0 md:p-4 flex flex-col pt-2">
        <!-- Tab 切换：单人/多人 - 移到最外层，始终可见可点击 -->
        <div class="flex gap-2 mb-4 pl-4">
            <button
                @click="swapMode = 'single'"
                class="px-4 py-2 rounded-lg font-medium transition-all"
                :class="swapMode === 'single' ? 'bg-orange-600 text-white' : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'"
            >
                V1.0
            </button>
            <!-- V2.0 Beta 暂时关闭,后续接入再放开
            <button
                @click="handleMultipleModeClick"
                class="px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-1"
                :class="swapMode === 'multiple' ? 'bg-orange-600 text-white' : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'"
            >
                <span>V2.0 Beta</span>
                <svg @click.stop="showTipsDialog('v2beta')" class="cursor-pointer flex-shrink-0" width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.75 14.25C5.4362 14.25 2.75 11.5638 2.75 8.25C2.75 4.9362 5.4362 2.25 8.75 2.25C12.0638 2.25 14.75 4.9362 14.75 8.25C14.75 11.5638 12.0638 14.25 8.75 14.25ZM8.15 7.65V11.25H9.35V7.65H8.15ZM8.15 5.25V6.45H9.35V5.25H8.15Z" fill="currentColor" />
                </svg>
            </button>
            -->

        </div>

        <!-- 内容容器：相对定位，loading overlay 只覆盖这个区域 -->
        <div class="relative flex-1 flex flex-col">
            <!-- loading overlay - 只覆盖内容区，不影响 tabs -->
            <div v-if="loading || showFacePickerLoading"
                 class="absolute inset-0 bg-neutral-900/80 backdrop-blur-sm flex items-center justify-center z-50 rounded-xl">
                <div class="bg-neutral-900 flex flex-col items-center justify-center w-[108px] h-[108px] rounded-2xl p-4">
                    <v-progress-circular color="primary" indeterminate></v-progress-circular>
                    <div class="msg mt-4">{{ showFacePickerLoading ? 'Detecting faces...' : 'loading...' }}</div>
                </div>
            </div>

            <!-- 多人模式：Face Detected -->
            <template v-if="swapMode === 'multiple'">
                <div class="title font-semibold text-md md:text-lg my-2 ml-4 md:my-5">Faces Detected</div>
                <div v-if="faceCouples.length === 0" class="p-4 bg-neutral-800 rounded-xl mb-4 text-center text-neutral-400">
                    <p>No faces detected in the uploaded material.</p>
                    <p class="text-sm mt-2">Please try uploading a clearer image or video with visible faces.</p>
                </div>
                <div v-else class="couples flex flex-nowrap md:flex-wrap overflow-x-scroll md:overflow-x-hidden scroll-container gap-3 mb-4 ml-[15px] md:ml-0">
                    <AvatarCouple v-for="(faceCouple, index) in faceCouples" :key="index"
                        class="p-2 bg-neutral-800 rounded-2xl border-2 !border-[#2A2C32] flex items-center justify-center cursor-pointer transition-all flex-shrink-0"
                        :class="{ '!border-orange-500': selectedCoupleIndex === index }" @click="selectedCoupleIndex = index"
                        :left-src="faceCouple.left" :left-coord="faceCouple.coord" v-model:right-src="faceCouple.right"
                        deletable
                        @face-uploaded="handleFaceUploaded" />
                </div>
            </template>

            <!-- Choose a Face - 在多人模式且没有检测到人脸时隐藏 -->
            <div class="face-list" v-if="!(swapMode === 'multiple' && faceCouples.length === 0)">
                <div class="flex items-center my-2 md:my-5">
                    <div class="title font-semibold text-md md:text-lg flex items-center justify-center md:justify-start w-full">
                        <span class="inline-flex items-center">Select face you want to swap</span>
                        <svg @click="showTipsDialog('info')" class="ml-2 cursor-pointer" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.75 14.25C5.4362 14.25 2.75 11.5638 2.75 8.25C2.75 4.9362 5.4362 2.25 8.75 2.25C12.0638 2.25 14.75 4.9362 14.75 8.25C14.75 11.5638 12.0638 14.25 8.75 14.25ZM8.15 7.65V11.25H9.35V7.65H8.15ZM8.15 5.25V6.45H9.35V5.25H8.15Z" fill="#888A86" />
                        </svg>
                    </div>
                </div>
                <!-- empty -->
                <template v-if="faces.length === 0">
                    <template v-if="!isMobile">
                        <div :class="{ 'bg-neutral-800': isDragging }" class="border-orange-600 border border-dashed rounded-xl h-auto md:h-80 flex flex-col items-center justify-center" @dragover="handleDragOver" @drop="handleDrop" @dragleave="handleDragLeave">
                            <img src="/public/icon/feature_01.png" class="w-16 h-16">
                            <div class="mt-4">Drag & drop upload your Face</div>
                            <div class="text-xs text-neutral-500 mb-4"> Max 20 MB (PNG, JPEG)</div>
                            <v-btn style="color: #000 !important;" color="primary" @click="showTipsDialog('upload')">
                                <img src="/public/icon/upload_face.svg" alt="" class="mr-1 relative top-[-1px]">
                                <span>Upload Face</span>
                            </v-btn>
                        </div>
                    </template>
                    <template v-else>
                        <div class=" rounded-xl flex flex-col items-center justify-center">
                            <div class="rounded-full w-16 h-16 object-cover object-center mx-3 my-2 ml-0 md:ml-3 cursor-pointer border-2 border-dashed border-orange-600 flex items-center justify-center" @click="triggerFileInput">
                                <img src="/public/icon/face_add.svg" alt="" class="w-2/3 h-2/3">
                            </div>
                        </div>
                    </template>
                </template>
                <template v-else>
                <div class="content flex flex-nowrap md:grid md:grid-cols-4 md:pr-4 max-h-[238px] overflow-x-scroll md:overflow-y-auto md:overflow-x-hidden scroll-container ml-[15px] md:ml-0">
                    <div class="sticky -left-[0.5px] px-[1px] z-10 w-16 h-16 mr-2 ml-0 md:ml-2 md:mx-3 my-2 bg-neutral-900">
                            <div class=" rounded-full w-[62px] h-[62px] object-cover object-center  transition-all  flex-shrink-0 duration-75 cursor-pointer border-2 border-dashed border-orange-600 p-2.5  overflow-hidden">
                                <img src="/public/icon/face_add.svg" alt="" @click="showTipsDialog('upload')">
                            </div>
                    </div>
                    <div v-for="face in faces" :key="face.face_id" :class="{ '!border-[#22C55E] border-2': currFace?.url === face.url }" class="w-16 h-16 mx-2 md:mx-3 my-2 transition-all duration-75 cursor-pointer flex-shrink-0 rounded-full  relative">
                            <img :src="face.url" alt="" @click="handleFaceSelection(face)" class="w-full h-full object-cover object-center rounded-full" @dragstart="event => event.preventDefault()">
                            <img v-if="currFace?.url === face.url" src="/public/icon/ic_correct.svg" class="w-5 h-5 absolute right-0 bottom-0" alt="">
                    </div>
                </div>
                </template>
            </div>

            <!-- Action Button -->
            <div class="row flex flex-col items-center justify-center mt-auto px-4 md:px-0">
                <v-btn @click="onFaceSwap" class="mt-6 w-full bg-gradient-to-bl from-amber-500 to-orange-600" size="large" :disabled="!hasSelectedFace || (swapMode === 'multiple' && faceCouples.length === 0)" style="color: #000 !important;" append-icon="mdi-arrow-right">
                    <span>Swap Faces</span>
                    <span class="text-sm">({{ creditNum || 0 }} {{ creditNum > 1 ? 'credits' : 'credit' }})</span>
                </v-btn>
                <p v-if="freeRemaining > 0" class="text-xs text-emerald-400 mt-2 mb-4">
                    {{ freeRemaining }} free today · no credit will be deducted
                </p>
                <div v-else class="mb-6"></div>
            </div>
        </div>

        <input type="file" @change="handleFileChange" style="display: none;" ref="fileInput" />

        <!-- Model -->
        <!-- <div class="row">
            <div class="title font-extrabold text-lg flex items-center my-5">
                <span class="mr-2">Model</span>
                <v-icon icon="mdi-information-slab-circle-outline"></v-icon>
            </div>
        </div>
        <div class="model-content font-medium md:font-semibold text-lg w-full grid grid-cols-2 gap-x-2">
            <div v-for="item in models" :key="item.value" class=" " @click="selectModel(item.value)">
                <span :class="{ 'selected-model-btn': selectedModel === item.value }"
                    class="bg-neutral-800 px-2 py-4 rounded-full flex items-center justify-center cursor-pointer border-2 border-[#2a2c32] w-full  mb-4 h-[40px] text-ellipsis overflow-hidden text-nowrap text-sm sm:text-lg">
                    {{ item.label }}
                </span>
            </div>
        </div> -->

        <!-- HD Face -->
        <!-- <div class="flex items-center font-extrabold text-lg mt-5">
            <span class="mr-2">HD Face</span>
            <v-icon icon="mdi-information-slab-circle-outline"></v-icon>
            <v-switch class="ml-auto h-12" color="#F06906"></v-switch>
        </div> -->

        <TipsDialog
            v-model="tipsDialogVisible"
            :dialog-type="dialogType"
            :show-bottom-btn="dialogType === 'upload' || dialogType === 'v2beta'"
            :show-toggle="dialogType === 'info' || faces.length > 0"
            @confirm="handleTipsDialogConfirm"
        />

    </v-container>
</template>

<style lang="scss" scoped>
.selected-model-btn {
    border: 2px solid #F06906 !important;
}

/* 定义整个滚动条宽度 */
.scroll-container::-webkit-scrollbar {
    width: 4px;
    height: 4px;
}

@media (max-width: 768px) {
    .scroll-container::-webkit-scrollbar {
        height: 0;
    }
}

/* 滚动条滑块部分的样式 */
.scroll-container::-webkit-scrollbar-thumb {
    background-color: #404040;
    border-radius: 4px;
}

/* 滚动条轨道部分的样式 */
.scroll-container::-webkit-scrollbar-track {
    background: #262626;
    border-radius: 4px;
}

/* 滚动条的右侧阴影 */
.scroll-container::-webkit-scrollbar-track-piece {
    background: #262626;
    border-radius: 4px;
}
</style>
